import React, { useMemo, useState, useEffect } from "react";
import { AnalyticsData } from "../Constant";
import { CategoriesData } from "../../../../redux/MerchantAnalyticsOverview";
import { ChartContainer } from "../styles";
import { CHART_COLORS } from "../Constant";

interface CategoryDistributionChartProps {
  analyticsData: AnalyticsData;
  categoriesData?: CategoriesData | null;
  isDarkMode?: boolean;
  loading?: boolean;
}

const CategoryDistributionChart: React.FC<CategoryDistributionChartProps> = ({
  analyticsData,
  categoriesData,
  isDarkMode = false,
  loading = false,
}) => {
  const [ReactECharts, setReactECharts] = useState<any>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    import("echarts-for-react").then((module) => {
      setReactECharts(() => module.default);
    });
  }, []);

  const option = useMemo(() => {
    const categories = categoriesData?.categories || [];
    const data = categories
      .filter((item) => item.name && item.sales > 0)
      .map((item) => ({
        name: item.name!,
        value: item.sales,
        orderCount: 0,
      }))
      .sort((a, b) => b.value - a.value);
    
    const useBarChart = data.length > 5;

    if (useBarChart) {
      return {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          backgroundColor: isDarkMode ? "#1a202c" : "#fff",
          borderColor: isDarkMode ? "#4a5568" : "#e2e8f0",
          textStyle: {
            color: isDarkMode ? "#fff" : "#1a202c",
          },
          formatter: (params: any) => {
            const param = params[0];
            const item = data.find(d => d.name === param.name);
            return `${param.name}<br/>Sales: $${param.value.toFixed(2)}<br/>Orders: ${item?.orderCount || 0}`;
          },
        },
        grid: {
          left: "20%",
          right: "4%",
          bottom: "3%",
          containLabel: false,
        },
        xAxis: {
          type: "value",
          axisLine: {
            lineStyle: {
              color: isDarkMode ? "#4a5568" : "#d9d9d9",
            },
          },
          axisLabel: {
            color: isDarkMode ? "#a0aec0" : "#718096",
            formatter: (value: number) => `$${value.toLocaleString()}`,
          },
          splitLine: {
            lineStyle: {
              color: isDarkMode ? "#2d3748" : "#f0f0f0",
            },
          },
        },
        yAxis: {
          type: "category",
          data: data.map(item => item.name),
          axisLine: {
            lineStyle: {
              color: isDarkMode ? "#4a5568" : "#d9d9d9",
            },
          },
          axisLabel: {
            color: isDarkMode ? "#a0aec0" : "#718096",
          },
        },
        series: [
          {
            name: "Category Sales",
            type: "bar",
            data: data.map((item, index) => ({
              value: item.value,
              itemStyle: {
                color: CHART_COLORS[index % CHART_COLORS.length],
                borderRadius: [0, 8, 8, 0],
              },
            })),
            emphasis: {
              focus: "series",
            },
          },
        ],
      };
    }

    return {
      tooltip: {
        trigger: "item",
        backgroundColor: isDarkMode ? "#1a202c" : "#fff",
        borderColor: isDarkMode ? "#4a5568" : "#e2e8f0",
        textStyle: {
          color: isDarkMode ? "#fff" : "#1a202c",
        },
        formatter: (params: any) => {
          const item = data.find(d => d.name === params.name);
          return `${params.name}<br/>Sales: $${params.value.toFixed(2)}<br/>Orders: ${item?.orderCount || 0}<br/>${params.percent}%`;
        },
      },
      legend: {
        orient: "vertical",
        left: "left",
        bottom: "bottom",
        textStyle: {
          color: isDarkMode ? "#a0aec0" : "#718096",
        },
      },
      series: [
        {
          name: "Category Sales",
          type: "pie",
          radius: ["40%", "70%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: isDarkMode ? "#1a202c" : "#fff",
            borderWidth: 2,
          },
          label: {
            show: true,
            formatter: (params: any) =>
              `${params.name}: $${params.value.toFixed(2)}`,
            color: isDarkMode ? "#fff" : "#1a202c",
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: "bold",
            },
          },
          data: data,
          color: CHART_COLORS,
        },
      ],
    };
  }, [isDarkMode, categoriesData]);

  if (!isMounted || !ReactECharts || loading) {
    return (
      <ChartContainer>
        <div style={{ height: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {!isMounted || !ReactECharts ? "Loading chart..." : "Loading categories data..."}
        </div>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <ReactECharts option={option} style={{ height: "100%", width: "100%" }} />
    </ChartContainer>
  );
};

export default CategoryDistributionChart;